import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { Stack } from 'src/core/ui';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { AddToGroupListingsModal } from 'src/modals/GroupListings';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  MoveListingGroupDialog,
  MoveListingGroupItem,
} from 'src/modals/GroupListingsV2/components/GroupsPreview/MoveListingGroupDialog';
import { ContentId } from 'src/utils/constants/contentId';
import {
  CommandOptionType,
  EventWithData,
  Listing,
  ListingGroup,
  ListingGroupItemInput,
} from 'src/WebApiController';

export const useBulkMoveToGroup = (eventData: EventWithData) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const { setModal } = useContext(ModalContext);

  const { openDialog, closeDialog } = useDialog(
    DialogId.AddToGroup,
    MoveListingGroupDialog
  );

  const { filterQueryWithEventIds, isAllListingsSelected } =
    useInventoryEventBulkActionsState(eventData.event);

  const eventListings = useMemo(() => {
    return (
      (eventData?.entities?.listings?.flatMap(
        flattenListingGroup
      ) as Listing[]) ?? []
    );
  }, [eventData?.entities?.listings]);

  const selectedListingIds = useMemo(() => {
    if (isAllListingsSelected) {
      return filterQueryWithEventIds.entityIds?.length
        ? filterQueryWithEventIds.entityIds
        : eventListings.map((listing) => listing.id);
    }
    return filterQueryWithEventIds.entityIds ?? [];
  }, [eventListings, filterQueryWithEventIds.entityIds, isAllListingsSelected]);

  const eventListingGroups = useMemo(() => {
    if (!eventData?.entities?.listings) {
      return [];
    }

    return eventData.entities.listings
      .filter((l): l is ListingGroup => l.isLtGrp && l.ltGrpId != null)
      .map((group) => {
        const items = flattenListingGroup(group).map(
          (item) =>
            ({
              listingId: item.id,
              priority: item.ltGrpPrior,
              groupId: item.ltGrpId,
            }) as ListingGroupItemInput
        );
        return {
          listingGroupId: group.ltGrpId!,
          name: group.groupName,
          listingGroupItems: items,
        } as MoveListingGroupItem;
      });
  }, [eventData?.entities?.listings]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      openDialog({
        listingGroups: eventListingGroups,
        listings: eventListings,
        canAddNewGroup: true,
        onMoveListingToGroup: (
          groupId: string,
          groupName?: string,
          inheritListingGroupSettings?: boolean,
          maintainPriceStagger?: boolean
        ) => {
          const existingGroup = eventListingGroups.find(
            (group) => group.listingGroupId === groupId
          );
          const newListingGroupName = existingGroup ? undefined : groupName;
          closeDialog();
          setModal({
            children: (
              <AddToGroupListingsModal
                selectedListingIds={selectedListingIds}
                listingGroupId={groupId ?? undefined}
                viagVirtId={eventData.event.virtId}
                newListingGroupName={newListingGroupName}
                inheritListingGroupSettings={inheritListingGroupSettings}
                maintainPriceStagger={maintainPriceStagger}
              />
            ),
            clickOutsideToClose: true,
            size: 'slide-in',
          });
        },
        onClose: () => {
          closeDialog();
        },
      });
    },
    [
      closeDialog,
      eventData.event.virtId,
      eventListingGroups,
      eventListings,
      openDialog,
      selectedListingIds,
      setModal,
    ]
  );

  const onLaunchDialogStableRef = useEventCallback(() => {
    openDialog({
      listingGroups: eventListingGroups,
      listings: eventListings,
      canAddNewGroup: true,
      onMoveListingToGroup: (
        groupId: string,
        groupName?: string,
        inheritListingGroupSettings?: boolean,
        maintainPriceStagger?: boolean
      ) => {
        const existingGroup = eventListingGroups.find(
          (group) => group.listingGroupId === groupId
        );
        const newListingGroupName = existingGroup ? undefined : groupName;
        closeDialog();
        setModal({
          children: (
            <AddToGroupListingsModal
              selectedListingIds={selectedListingIds}
              listingGroupId={groupId ?? undefined}
              viagVirtId={eventData.event.virtId}
              newListingGroupName={newListingGroupName}
              inheritListingGroupSettings={inheritListingGroupSettings}
              maintainPriceStagger={maintainPriceStagger}
            />
          ),
          clickOutsideToClose: true,
          size: 'slide-in',
        });
      },
      onClose: () => {
        closeDialog();
      },
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditMoveToGroup,
        onSelect: onLaunchDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditMoveToGroup,
      });
    };
  }, [onLaunchDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo(() => {
    return [{ title: ContentId.MoveTo, onClick: onLaunchDialog }];
  }, [onLaunchDialog]);

  return {
    dropdown: (
      <PosDropdownItem key="MoveToGroup" onClick={onLaunchDialog}>
        <Stack alignItems="center" gap="m" width="full">
          <Content id={ContentId.MoveTo} />
        </Stack>
      </PosDropdownItem>
    ),
    actionItems,
  };
};
